import { useEffect, useState, type FormEvent } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { MonitoringPredicate, type EntityKind, type MonitoringJob, type MonitoringRule, type MonitoringRun } from '@regigov/contracts';
import { monitoringApi } from '../api/compliance';
import { useAuth } from '../auth/AuthContext';
import { isCerAdminRole, isCerRole } from '../auth/roles';
import { DefinitionList, Field, ProblemAlert, formatDateTime } from './ui';

const BASE = '/cer/compliance/monitoring';
const REGISTER_PATH: Record<EntityKind, string> = { model: 'models', unit: 'units', installation: 'installations' };
const today = () => new Date().toISOString().slice(0, 10);

function EntityLink({ kind, id, label }: { kind: EntityKind; id: string; label: string }) {
  return <Link to={`/cer/register/${REGISTER_PATH[kind]}/${id}`}>{label}</Link>;
}

/** CER monitoring: rules (every version), a run form per rule, and the retained run history. */
export function MonitoringPage() {
  const { user } = useAuth();
  const canConfigure = isCerAdminRole(user?.roles);
  const rules = useQuery({ queryKey: ['monitoring', 'rules'], queryFn: monitoringApi.rules });
  const runs = useQuery({ queryKey: ['monitoring', 'runs'], queryFn: () => monitoringApi.runs() });
  const jobs = useQuery({
    queryKey: ['monitoring', 'jobs'],
    queryFn: monitoringApi.jobs,
    refetchInterval: (q) => (q.state.data?.items.some((j) => j.status === 'running') ? 2000 : false),
  });
  const qc = useQueryClient();
  const runningCount = jobs.data?.items.filter((j) => j.status === 'running').length ?? 0;
  const [lastRunning, setLastRunning] = useState(runningCount);
  useEffect(() => {
    if (runningCount < lastRunning) void qc.invalidateQueries({ queryKey: ['monitoring', 'runs'] });
    setLastRunning(runningCount);
  }, [runningCount, lastRunning, qc]);
  const [editing, setEditing] = useState<MonitoringRule | 'new' | null>(null);

  const byCode = new Map<string, MonitoringRule[]>();
  for (const r of rules.data?.items ?? []) byCode.set(r.code, [...(byCode.get(r.code) ?? []), r]);

  return (
    <>
      <div className="row">
        <h1>Compliance monitoring</h1>
        <Link to="/cer/compliance/risk">Risk view →</Link>
        {canConfigure && (
          <button type="button" onClick={() => setEditing('new')}>
            New monitoring rule
          </button>
        )}
      </div>
      <p className="notice">
        Monitoring rules are effective-dated predicates over the register. A run checks every record as it stood on the as-of date, as CER knew it at the known-at time, and
        keeps each hit with the exact history version it matched. Runs never change the register; re-running with an earlier known-at reproduces that result.
      </p>
      <ProblemAlert error={rules.error ?? runs.error ?? jobs.error} />
      {editing && <MonitoringRuleEditor key={editing === 'new' ? 'new' : editing.id} base={editing === 'new' ? null : editing} onDone={() => setEditing(null)} />}
      {[...byCode.entries()].map(([code, versions]) => {
        const current = versions.find((r) => !r.validTo) ?? versions[0]!;
        return (
          <section className="card" key={code}>
            <div className="row">
              <h2>
                {current.title} <code>{code}</code>
              </h2>
              {canConfigure && (
                <button type="button" className="linklike" onClick={() => setEditing(current)}>
                  New version from v{current.version}
                </button>
              )}
            </div>
            <p>{current.description}</p>
            <table className="table">
              <thead>
                <tr>
                  <th>Version</th>
                  <th>In force</th>
                  <th>Scope</th>
                  <th>Published</th>
                  <th>Reason</th>
                  <th>Predicate</th>
                </tr>
              </thead>
              <tbody>
                {versions.map((r) => (
                  <tr key={r.id}>
                    <td>v{r.version}</td>
                    <td>
                      {r.validFrom} → {r.validTo ?? <em>open</em>}
                    </td>
                    <td>
                      {r.entityKind}
                      {r.deviceClass && <span className="muted"> · {r.deviceClass}</span>}
                    </td>
                    <td>
                      {formatDateTime(r.createdAt)} <span className="muted">{r.createdByName ?? 'seed'}</span>
                    </td>
                    <td>{r.changeReason}</td>
                    <td>
                      <details>
                        <summary className="muted">predicate</summary>
                        <pre>{JSON.stringify(r.predicate, null, 2)}</pre>
                      </details>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            <RunForm ruleCode={code} />
          </section>
        );
      })}
      {rules.data && byCode.size === 0 && <p className="muted">No monitoring rules yet.</p>}
      {jobs.data && jobs.data.items.length > 0 && (
        <>
          <h2>Runs in progress</h2>
          <JobTable jobs={jobs.data.items} />
        </>
      )}
      <h2>Run history</h2>
      <RunTable runs={runs.data?.items ?? []} />
    </>
  );
}

function RunTable({ runs }: { runs: MonitoringRun[] | Omit<MonitoringRun, 'rule' | 'hits' | 'previous'>[] }) {
  if (runs.length === 0) return <p className="muted">No runs yet.</p>;
  return (
    <table className="table">
      <thead>
        <tr>
          <th>Run</th>
          <th>Rule</th>
          <th>As of</th>
          <th>Known at</th>
          <th>Checked</th>
          <th>Hits</th>
          <th>Run by</th>
        </tr>
      </thead>
      <tbody>
        {runs.map((r) => (
          <tr key={r.id}>
            <td>
              <Link to={`${BASE}/runs/${r.id}`}>#{r.id}</Link>
            </td>
            <td>
              <code>{r.ruleCode}</code> v{r.ruleVersion}
            </td>
            <td>{r.asOf}</td>
            <td>{formatDateTime(r.knownAt)}</td>
            <td>{r.population}</td>
            <td>
              <strong>{r.hitCount}</strong>
            </td>
            <td>
              {r.runByName ?? '—'} <span className="muted">{formatDateTime(r.runAt)}</span>
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

const plural = (n: number, word: string) => `${n.toLocaleString()} ${word}${n === 1 ? '' : 's'}`;

function JobProgress({ job }: { job: MonitoringJob }) {
  const done = job.status === 'completed';
  const pct = job.total > 0 ? Math.min(99, Math.floor((job.scanned / job.total) * 100)) : null;
  return (
    <div>
      {job.status === 'running' && (pct === null ? <progress className="progress" /> : <progress className="progress" max={100} value={pct} aria-label={`${pct}% checked`} />)}
      <div className="muted">
        {job.scanned.toLocaleString()} of {done ? '' : 'about '}
        {plural(job.total, job.entityKind)} read{pct !== null && job.status === 'running' && ` (${pct}%)`} · {job.population.toLocaleString()} in scope · {plural(job.hitCount, 'hit')}
        {job.status === 'running' && ' so far'}
      </div>
    </div>
  );
}

function JobTable({ jobs }: { jobs: MonitoringJob[] }) {
  return (
    <table className="table">
      <thead>
        <tr>
          <th>Job</th>
          <th>Rule</th>
          <th>As of</th>
          <th>Status</th>
          <th>Progress</th>
          <th>Started by</th>
        </tr>
      </thead>
      <tbody>
        {jobs.map((j) => (
          <tr key={j.id}>
            <td>
              <Link to={`${BASE}/jobs/${j.id}`}>Job {j.id}</Link>
            </td>
            <td>
              <code>{j.ruleCode}</code>
            </td>
            <td>{j.asOf}</td>
            <td>{j.status === 'failed' ? <span className="error">failed</span> : j.status}</td>
            <td>{j.status === 'failed' ? j.error : <JobProgress job={j} />}</td>
            <td>
              {j.requestedByName ?? '—'} <span className="muted">{formatDateTime(j.createdAt)}</span>
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

const elapsed = (job: MonitoringJob) => {
  const s = Math.max(0, Math.round(((job.finishedAt ? new Date(job.finishedAt).getTime() : Date.now()) - new Date(job.createdAt).getTime()) / 1000));
  return s < 60 ? `${s}s` : `${Math.floor(s / 60)}m ${s % 60}s`;
};

/** A run started in the background: polls the job until the run is saved, then opens it. */
export function MonitoringJobPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const query = useQuery({
    queryKey: ['monitoring', 'jobs', Number(id)],
    queryFn: () => monitoringApi.job(Number(id)),
    refetchInterval: (q) => (q.state.data && q.state.data.status !== 'running' ? false : 1000),
  });
  const job = query.data;
  useEffect(() => {
    if (job?.status !== 'completed' || job.runId === null) return;
    void qc.invalidateQueries({ queryKey: ['monitoring'] });
    navigate(`${BASE}/runs/${job.runId}`, { replace: true });
  }, [job?.status, job?.runId, navigate, qc]);
  return (
    <>
      <p className="crumbs">
        <Link to={BASE}>Compliance monitoring</Link> / Job {id}
      </p>
      <ProblemAlert error={query.error} />
      {job && (
        <>
          <h1>
            Monitoring run <code>{job.ruleCode}</code> {job.status === 'running' ? 'in progress' : job.status}
          </h1>
          <DefinitionList
            items={[
              ['As of', job.asOf],
              ['Known at', job.knownAt ? formatDateTime(job.knownAt) : 'What CER knew when the run started'],
              ['Started', `${formatDateTime(job.createdAt)} by ${job.requestedByName ?? '—'}`],
              ['Elapsed', elapsed(job)],
            ]}
          />
          <JobProgress job={job} />
          {job.status === 'running' && <p className="muted">The run continues if you leave this page; it is listed under Runs in progress on the monitoring page.</p>}
          {job.status === 'failed' && (
            <div role="alert" className="alert">
              <strong>The run failed</strong>
              <p>{job.error}</p>
            </div>
          )}
        </>
      )}
    </>
  );
}

function RunForm({ ruleCode }: { ruleCode: string }) {
  const { user } = useAuth();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [asOf, setAsOf] = useState(today());
  const [knownAt, setKnownAt] = useState('');
  const run = useMutation({
    mutationFn: () => monitoringApi.start({ ruleCode, asOf, ...(knownAt && { knownAt: new Date(knownAt).toISOString() }) }),
    onSuccess: (job) => {
      void qc.invalidateQueries({ queryKey: ['monitoring', 'jobs'] });
      navigate(`${BASE}/jobs/${job.id}`);
    },
  });
  if (!isCerRole(user?.roles)) return null;
  const onSubmit = (e: FormEvent) => {
    e.preventDefault();
    run.mutate();
  };
  return (
    <form className="row" onSubmit={onSubmit}>
      <Field label="As of">
        <input type="date" value={asOf} onChange={(e) => setAsOf(e.target.value)} required />
      </Field>
      <Field label="Known at (optional)" hint="Leave blank for what CER knows now">
        <input type="datetime-local" value={knownAt} onChange={(e) => setKnownAt(e.target.value)} />
      </Field>
      <button type="submit" disabled={run.isPending}>
        {run.isPending ? 'Starting…' : 'Run'}
      </button>
      <ProblemAlert error={run.error} />
    </form>
  );
}

function MonitoringRuleEditor({ base, onDone }: { base: MonitoringRule | null; onDone: () => void }) {
  const qc = useQueryClient();
  const [code, setCode] = useState(base?.code ?? '');
  const [title, setTitle] = useState(base?.title ?? '');
  const [description, setDescription] = useState(base?.description ?? '');
  const [entityKind, setEntityKind] = useState<EntityKind>(base?.entityKind ?? 'installation');
  const [deviceClass, setDeviceClass] = useState(base?.deviceClass ?? '');
  const [validFrom, setValidFrom] = useState('');
  const [changeReason, setChangeReason] = useState('');
  const [predicate, setPredicate] = useState(JSON.stringify(base?.predicate ?? { all: [{ field: 'status', op: 'eq', value: 'active' }] }, null, 2));
  const [localError, setLocalError] = useState<string | null>(null);
  const publish = useMutation({
    mutationFn: monitoringApi.publish,
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['monitoring', 'rules'] });
      onDone();
    },
  });
  const onSubmit = (e: FormEvent) => {
    e.preventDefault();
    setLocalError(null);
    let parsed: unknown;
    try {
      parsed = JSON.parse(predicate);
    } catch (err) {
      setLocalError(`Predicate is not valid JSON: ${(err as Error).message}`);
      return;
    }
    const checked = MonitoringPredicate.safeParse(parsed);
    if (!checked.success) {
      setLocalError(`Predicate is not valid: ${checked.error.issues.map((i) => `${i.path.join('.') || 'predicate'}: ${i.message}`).join('; ')}`);
      return;
    }
    publish.mutate({ code, title, description, entityKind, deviceClass: deviceClass || null, predicate: checked.data, validFrom, changeReason });
  };
  return (
    <form className="form card" onSubmit={onSubmit} noValidate>
      <h2>{base ? `New version of ${base.code}` : 'New monitoring rule'}</h2>
      <Field label="Code">
        <input value={code} onChange={(e) => setCode(e.target.value.toUpperCase())} disabled={!!base} placeholder="MR-BAT-GRID" />
      </Field>
      <Field label="Title">
        <input value={title} onChange={(e) => setTitle(e.target.value)} />
      </Field>
      <Field label="Description">
        <textarea value={description} onChange={(e) => setDescription(e.target.value)} rows={2} />
      </Field>
      <Field label="Record kind">
        <select value={entityKind} onChange={(e) => setEntityKind(e.target.value as EntityKind)}>
          <option value="model">Model</option>
          <option value="unit">Unit</option>
          <option value="installation">Installation</option>
        </select>
      </Field>
      <Field label="Device class (optional)">
        <input value={deviceClass} onChange={(e) => setDeviceClass(e.target.value)} placeholder="battery" />
      </Field>
      <Field label="Valid from" hint={base ? `Must be after ${base.validFrom}` : undefined}>
        <input type="date" value={validFrom} onChange={(e) => setValidFrom(e.target.value)} />
      </Field>
      <Field label="Predicate (JSON)" hint='Conditions {"field","op","value"} combined with all / any / not. Ops: eq, neq, gt, gte, lt, lte, in, exists, missing.'>
        <textarea value={predicate} onChange={(e) => setPredicate(e.target.value)} rows={10} spellCheck={false} />
      </Field>
      <Field label="Reason for change">
        <input value={changeReason} onChange={(e) => setChangeReason(e.target.value)} />
      </Field>
      {localError && <p className="error">{localError}</p>}
      <ProblemAlert error={publish.error} />
      <div className="row">
        <button type="submit" disabled={publish.isPending}>
          Publish
        </button>
        <button type="button" className="linklike" onClick={onDone}>
          Cancel
        </button>
      </div>
    </form>
  );
}

/** One retained run: the rule version applied, its hits with links to the register, and the change since the previous run. */
export function MonitoringRunPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const query = useQuery({ queryKey: ['monitoring', 'runs', Number(id)], queryFn: () => monitoringApi.run(Number(id)) });
  const reproduce = useMutation({
    mutationFn: (r: MonitoringRun) => monitoringApi.start({ ruleCode: r.ruleCode, asOf: r.asOf, knownAt: r.knownAt }),
    onSuccess: (job) => {
      void qc.invalidateQueries({ queryKey: ['monitoring', 'jobs'] });
      navigate(`${BASE}/jobs/${job.id}`);
    },
  });
  const r = query.data;
  return (
    <>
      <p className="crumbs">
        <Link to={BASE}>Compliance monitoring</Link> / Run #{id}
      </p>
      <ProblemAlert error={query.error ?? reproduce.error} />
      {r && (
        <>
          <div className="row">
            <h1>
              Run #{r.id} — {r.ruleTitle}
            </h1>
            <button type="button" onClick={() => reproduce.mutate(r)} disabled={reproduce.isPending} title="Run again with the same as-of date and known-at time">
              Reproduce this run
            </button>
          </div>
          <DefinitionList
            items={[
              ['Rule', <><code>{r.ruleCode}</code> v{r.ruleVersion} (in force {r.rule.validFrom} → {r.rule.validTo ?? 'open'})</>],
              ['As of', r.asOf],
              ['Known at', formatDateTime(r.knownAt)],
              ['Run', `${formatDateTime(r.runAt)} by ${r.runByName ?? '—'}`],
              ['Records checked', `${r.population} ${r.rule.entityKind}${r.population === 1 ? '' : 's'}${r.rule.deviceClass ? ` (${r.rule.deviceClass})` : ''}`],
              ['Hits', String(r.hitCount)],
            ]}
          />
          <details>
            <summary className="muted">Predicate applied</summary>
            <pre>{JSON.stringify(r.rule.predicate, null, 2)}</pre>
          </details>
          {r.previous && (
            <section className="card">
              <h2>Compared with run #{r.previous.run.id}</h2>
              <p className="muted">
                <Link to={`${BASE}/runs/${r.previous.run.id}`}>Run #{r.previous.run.id}</Link> (as of {r.previous.run.asOf}, known at {formatDateTime(r.previous.run.knownAt)}) had{' '}
                {r.previous.run.hitCount} hit{r.previous.run.hitCount === 1 ? '' : 's'}.
              </p>
              <p>
                No longer flagged:{' '}
                {r.previous.resolved.length === 0 ? <em>none</em> : r.previous.resolved.map((h, i) => <span key={h.entityId}>{i > 0 && ', '}<EntityLink kind={h.entityKind} id={h.entityId} label={h.label} /></span>)}
              </p>
              <p>
                Newly flagged:{' '}
                {r.previous.added.length === 0 ? <em>none</em> : r.previous.added.map((h, i) => <span key={h.entityId}>{i > 0 && ', '}<EntityLink kind={h.entityKind} id={h.entityId} label={h.label} /></span>)}
              </p>
            </section>
          )}
          <h2>Hits</h2>
          {r.hits.length === 0 ? (
            <p className="muted">No records matched.</p>
          ) : (
            <table className="table">
              <thead>
                <tr>
                  <th>Record</th>
                  <th>Responsible participant</th>
                  <th>Version matched</th>
                  <th>Rule set applied</th>
                  <th>Evidence</th>
                </tr>
              </thead>
              <tbody>
                {r.hits.map((h) => (
                  <tr key={h.id}>
                    <td>
                      <EntityLink kind={h.entityKind} id={h.entityId} label={h.label} />
                      <div className="muted">
                        <code>{h.entityId}</code>
                      </div>
                    </td>
                    <td>{h.responsibleParticipantId ? <Link to={`/cer/participants/${h.responsibleParticipantId}`}>{h.responsibleParticipantName ?? h.responsibleParticipantId}</Link> : '—'}</td>
                    <td>
                      v{h.entityVersion} <span className="muted">from {formatDateTime(h.evidence.validFrom)}, recorded {formatDateTime(h.evidence.recordedAt)}</span>
                    </td>
                    <td>{h.evidence.ruleVersionApplied === null ? '—' : <Link to="/cer/register/rules">v{h.evidence.ruleVersionApplied}</Link>}</td>
                    <td>
                      <dl className="dl">
                        {Object.entries(h.evidence.fields).map(([k, v]) => (
                          <div key={k}>
                            <dt>
                              <code>{k}</code>
                            </dt>
                            <dd>{v === null ? <em>not declared</em> : JSON.stringify(v)}</dd>
                          </div>
                        ))}
                      </dl>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </>
      )}
    </>
  );
}
